import styled from '@emotion/styled'
import { ArrowUp, Sparkle } from 'lucide-react'
import { FormEvent, useContext, useState } from 'react'
import { PRIMARY_CTA_FILL_COLOR_VAR } from '../../context/CustomizationContextProvider'
import {
    inScrollingLayout,
    PAGE_WIDTH_VAR,
    pageBleed,
} from '../../style/scrollingLayout'
import { wideTwoColumn } from '../landing-page/products/productColumns'
import { BrandChatContext } from './brandChatContext'
import { inlineQuestionRows } from './inlineQuestions'
import { useBrandChatParam } from './useBrandChatParam'

/** Questions about this product, tapped or typed, that open the brand chat
 * already asked. */
export const BrandChatQuestions: React.FC<{
    productId: string
    title: string
    /** The inline product's own setting; ?brand-chat shows them everywhere. */
    enabled: boolean
}> = ({ productId, title, enabled }) => {
    const chat = useContext(BrandChatContext)
    const brandChatParam = useBrandChatParam()
    const [draft, setDraft] = useState('')
    if (!chat || !(enabled || brandChatParam)) return null
    const rows = inlineQuestionRows(
        title,
        chat.brief?.featuredProducts.find(
            (product) => product.shopifyId === productId,
        ),
    )
    const submit = (event: FormEvent) => {
        event.preventDefault()
        if (!draft.trim()) return
        chat.ask(draft, 'typed')
        setDraft('')
    }
    return (
        <Section aria-label="Ask about this product">
            <Scroller>
                {rows.map((row, index) => (
                    <Row key={index}>
                        {row.map(({ label, question }) => (
                            <Chip
                                key={label}
                                type="button"
                                onClick={() => chat.ask(question, 'suggestion')}
                            >
                                <Sparkle
                                    size={12}
                                    fill="currentColor"
                                    aria-hidden
                                />
                                {label}
                            </Chip>
                        ))}
                    </Row>
                ))}
            </Scroller>
            <Composer onSubmit={submit}>
                <Input
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder="Ask a question about this product"
                    aria-label="Ask a question about this product"
                    enterKeyHint="send"
                />
                <SendButton
                    type="submit"
                    aria-label="Send"
                    disabled={!draft.trim()}
                >
                    <ArrowUp size={16} aria-hidden />
                </SendButton>
            </Composer>
        </Section>
    )
}

const Section = styled.div`
    padding: 12px 0 16px;
    border-bottom: 1px solid #e5e7eb;
`

// What pageBleed pulls the track out by, whether or not the product insets
// itself (it zeroes the section gutter when it does). The last chip keeps that
// space after it; the first starts at the screen edge.
const BLEED_INSET = `calc((var(${PAGE_WIDTH_VAR}, 100%) - 100%) / 2)`

// Full bleed while the buy box spans the product: the rows run edge to edge.
// Once the buy box has its own column beside the gallery, they stay in it.
const Scroller = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    ...inScrollingLayout({
        ...pageBleed,
        paddingInlineStart: 0,
        paddingInlineEnd: BLEED_INSET,
    }),
    [wideTwoColumn]: {
        width: 'auto',
        marginInline: 0,
        paddingInline: 0,
        overflowX: 'visible',
    },
})

// Each row scrolls as one line on phones. In the desktop column it wraps, so a
// mouse never has to scroll sideways to reach a question.
const Row = styled.div({
    display: 'flex',
    gap: 8,
    width: 'max-content',
    [wideTwoColumn]: {
        width: 'auto',
        flexWrap: 'wrap',
    },
})

const chipFill = `color-mix(
    in srgb,
    var(${PRIMARY_CTA_FILL_COLOR_VAR}, #000000) 10%,
    #ffffff
)`

const Chip = styled.button`
    appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 32px;
    padding: 8px 12px;
    border: 0;
    border-radius: 14px;
    background: ${chipFill};
    color: #232e34;
    font: inherit;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
`

const Composer = styled.form`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    padding: 4px 4px 4px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
`

const Input = styled.input`
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: none;
    color: #232e34;
    font: inherit;
    /* 16px keeps iOS from zooming the page on focus. */
    font-size: 16px;
`

const SendButton = styled.button`
    appearance: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border: 0;
    border-radius: 50%;
    background: ${chipFill};
    color: #232e34;
    cursor: pointer;
    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`
